import { useCallback, useState } from 'react';
import type { PnlQueryAnalysis } from 'src/components/star-grid/queryStats.types';
import { loadQueryAnalysis } from 'src/components/star-grid/services/queryStatsService';
import { errorMessage } from 'src/components/star-grid/utils/errorMessage';

export interface QueryAnalysisState {
  readonly analysis: PnlQueryAnalysis | undefined;
  readonly plans: readonly string[] | undefined;
  readonly loading: boolean;
  readonly error: string | undefined;
  readonly load: () => void;
}

const message = (caught: unknown): string => errorMessage(caught);

/**
 * Analyzes the last report query on request only: its stage breakdown from the profile StarRocks
 * already stored, and its plan. Neither re-runs the query, but both cost the FE a moment, so nothing
 * is fetched until someone asks.
 */
export const useQueryAnalysis = (explain: (() => Promise<readonly string[]>) | undefined, queryId: string | null | undefined): QueryAnalysisState => {
  const [analysis, setAnalysis] = useState<PnlQueryAnalysis>();
  const [plans, setPlans] = useState<readonly string[]>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const load = useCallback(() => {
    setLoading(true);
    setError(undefined);
    Promise.all([queryId ? loadQueryAnalysis(queryId).then(setAnalysis) : Promise.resolve(), explain ? explain().then(setPlans) : Promise.resolve()])
      .catch((caught: unknown) => setError(message(caught)))
      .finally(() => setLoading(false));
  }, [explain, queryId]);
  return { analysis, plans, loading, error, load };
};
